import React from 'react';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import { MONO_FONT_FAMILY } from 'styles/fonts';
import { registerJqLanguage } from '../jqLanguage';
import { CODE_EDITOR_MIN_HEIGHT } from './styles';

// The Code view: the jq filter in a Monaco editor.
const JqCodeEditor = ({
  codeText,
  isComplexJq,
  readOnly,
  height,
  onHeightChange,
  onChange,
}: {
  codeText: string;
  isComplexJq: boolean;
  readOnly: boolean;
  // Held by the parent so the height survives a trip through the Builder view.
  height: number;
  onHeightChange: (height: number) => void;
  onChange: (text: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {isComplexJq && (
        <div className="border-b border-ui-divider-lines bg-blue-4 px-3.5 py-2 text-body-sm text-blue-6">
          {t(
            'This filter contains complex expressions. Simplify the filter to use the visual Builder.',
          )}
        </div>
      )}
      {/* Grows with the filter, never shrinks below the size
          the panel has always had. No resize grip: the box
          now sizes itself, and a dragged height would be
          undone by the next keystroke. */}
      <div
        data-testid="JqFilterBuilder-code-editor-box"
        style={{ height, overflow: 'hidden' }}
      >
        <Editor
          height="100%"
          language="jq"
          value={codeText}
          theme="vs"
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineHeight: 20,
            padding: { top: 8 },
            readOnly,
            lineNumbers: 'on',
            wordWrap: 'on',
            automaticLayout: true,
            renderLineHighlight: 'none',
            fontFamily: MONO_FONT_FAMILY,
          }}
          beforeMount={registerJqLanguage}
          onMount={(e) => {
            const fit = () =>
              onHeightChange(
                Math.max(CODE_EDITOR_MIN_HEIGHT, e.getContentHeight()),
              );
            fit();
            // Fires for typed lines, wrapped lines and
            // programmatic value changes alike.
            e.onDidContentSizeChange(fit);
          }}
          onChange={(v) => onChange(v ?? '')}
        />
      </div>
    </>
  );
};

export default JqCodeEditor;
